/**
 * CommentsService — Firestore CRUD for AR animation comments.
 * Reads are public, writes require authenticated users with a profile account.
 */

// Firebase Firestore — types are inferred at runtime; explicit any used for SDK callback params
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import {
  getFirestore,
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { getFirebaseApp } from '../config/firebase.js'
import { requireAuthenticatedAccount } from '../config/firebase.js'

export interface Comment {
  id: string
  text: string
  username: string
  createdAt: Date
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _db: any = null

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function db(): any {
  if (!_db) {
    const app = getFirebaseApp()
    if (!app) throw new Error('Firebase app not initialized')
    _db = getFirestore(app)
  }
  return _db
}

/**
 * Post a comment anonymously under a given animation.
 * animationId is used as the Firestore document path segment.
 */
export async function postComment(animationId: string, text: string): Promise<void> {
  const trimmed = text.trim()
  if (!trimmed) return
  const { username } = await requireAuthenticatedAccount()
  const col = collection(db(), 'comments', animationId, 'posts')
  await addDoc(col, {
    text: trimmed,
    username,
    createdAt: serverTimestamp()
  })
}

/**
 * Subscribe to live comment updates for a given animation.
 * Returns an unsubscribe function — call it when the component unmounts.
 * onUpdate is called immediately with the current snapshot, then on each change.
 * On Firestore error the callback is called with [] and a warning is logged.
 */
export function subscribeComments(
  animationId: string,
  onUpdate: (comments: Comment[]) => void
): () => void {
  try {
    const q = query(
      collection(db(), 'comments', animationId, 'posts'),
      orderBy('createdAt', 'desc')
    )
    return onSnapshot(
      q,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (snap: any) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const comments: Comment[] = snap.docs.map((doc: any) => ({
          id: doc.id as string,
          text: (doc.data().text as string) ?? '',
          username: (doc.data().username as string) || 'Guest',
          createdAt: (doc.data().createdAt as Timestamp)?.toDate?.() ?? new Date()
        }))
        onUpdate(comments)
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (err: any) => {
        console.warn('[CommentsService] Firestore error — comments unavailable:', err.message)
        onUpdate([])
      }
    )
  } catch (err) {
    console.warn('[CommentsService] Failed to subscribe to comments:', err)
    onUpdate([])
    return () => {}
  }
}
